/**
 * Loading-state resolver for the scope-seeding layouts (customer / plant).
 *
 * A scope layout must render one of three things while it seeds its Redux
 * context: the shell loader, a bounded error state with retry, or its child
 * routes. This encodes that decision so a failed scope-seed fetch degrades to
 * an error the user can recover from instead of spinning forever.
 *
 * Contains:
 * - SCOPE_LOAD_STATE — the three terminal render states
 * - resolveScopeLoadState — pure {loading,error,ready} -> render-state resolver
 *
 * Returns plain JS — no JSX, no redux.
 */

export const SCOPE_LOAD_STATE = Object.freeze({
  LOADING: 'loading',
  ERROR: 'error',
  READY: 'ready',
});

/**
 * @param {{ loading: boolean, error: boolean, ready: boolean }} state
 *   loading — a scope-seed fetch is in flight.
 *   error   — a scope-seed fetch settled in failure (e.g. 403/5xx) and no
 *             retry is in flight.
 *   ready    — every seeded scope entity is present and matches the URL.
 * @returns {'loading'|'error'|'ready'} The state to render. A retry sets
 *   loading true and error false, so an in-flight retry resolves to LOADING;
 *   a settled failure with nothing ready resolves to ERROR (never an endless
 *   loader).
 */
export function resolveScopeLoadState({ loading, error, ready }) {
  if (loading) return SCOPE_LOAD_STATE.LOADING;
  if (error) return SCOPE_LOAD_STATE.ERROR;
  if (!ready) return SCOPE_LOAD_STATE.LOADING;
  return SCOPE_LOAD_STATE.READY;
}
